import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { ProjectTaskUserRole } from '@app/shared/types/enums/project-task-user-role.enum';
import { TaskUserRoleFilter } from '@app/shared/types/models/task/task-filters.model';
import { User } from '@app/shared/types/models/user/user.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { ProffeoUiSelectOption } from '@app/shared/ui/types/select.types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

export interface SelectUserRoleDialogData {
  availableUsers: User[];
  availableGroupAdmins: User[];
  availableIndustryAdmins: User[];
  selectedUserRole: TaskUserRoleFilter[];
}

@Component({
  selector: 'proffeo-select-user-role-dialog',
  templateUrl: './select-user-role-dialog.component.html',
  imports: [TranslatePipe, UiDialogComponent, UiButtonComponent, MatDialogClose, UiSelectComponent]
})
export class UserRoleFilterDialogComponent implements OnInit {
  private readonly dialogRef: MatDialogRef<UserRoleFilterDialogComponent, TaskUserRoleFilter[]> =
    inject<MatDialogRef<UserRoleFilterDialogComponent, TaskUserRoleFilter[]>>(MatDialogRef);
  private readonly translateService = inject(TranslateService);

  protected readonly ProjectTaskUserRole: typeof ProjectTaskUserRole = ProjectTaskUserRole;
  protected readonly data = inject<SelectUserRoleDialogData>(MAT_DIALOG_DATA);

  protected readonly userRoleOptions: ProffeoUiSelectOption<string | null>[] = this.buildUserOptions(
    this.data.availableUsers
  );
  protected readonly industryAdminOptions: ProffeoUiSelectOption<string | null>[] = this.buildUserOptions(
    this.data.availableIndustryAdmins
  );
  protected readonly groupAdminOptions: ProffeoUiSelectOption<string | null>[] = this.buildUserOptions(
    this.data.availableGroupAdmins
  );

  protected selectedUserUserId: WritableSignal<string | null> = signal<string | null>(null);
  protected selectedIndustryAdminUserId: WritableSignal<string | null> = signal<string | null>(null);
  protected selectedGroupAdminUserId: WritableSignal<string | null> = signal<string | null>(null);

  public ngOnInit(): void {
    const initiallySelectedUserRoles = this.data.selectedUserRole ?? [];
    initiallySelectedUserRoles.forEach(filterValue => this.applyFilterValue(filterValue));
  }

  private applyFilterValue(filterValue: TaskUserRoleFilter): void {
    switch (filterValue.role) {
      case ProjectTaskUserRole.USER:
        return this.selectedUserUserId.set(filterValue.applicationUserId);
      case ProjectTaskUserRole.INDUSTRY_ADMIN:
        return this.selectedIndustryAdminUserId.set(filterValue.applicationUserId);
      case ProjectTaskUserRole.GROUP_ADMIN:
        return this.selectedGroupAdminUserId.set(filterValue.applicationUserId);
    }
  }

  protected save(): void {
    const result: TaskUserRoleFilter[] = [
      { role: ProjectTaskUserRole.USER, applicationUserId: this.selectedUserUserId() },
      { role: ProjectTaskUserRole.INDUSTRY_ADMIN, applicationUserId: this.selectedIndustryAdminUserId() },
      { role: ProjectTaskUserRole.GROUP_ADMIN, applicationUserId: this.selectedGroupAdminUserId() }
    ];
    this.dialogRef.close(result);
  }

  private buildUserOptions(users: User[]): ProffeoUiSelectOption<string | null>[] {
    return [
      { value: null, label: this.translateService.instant('task-filters.user-role-none') },
      ...users.map(user => ({ value: user.id, label: user.fullName }))
    ];
  }
}
